import { Browser } from "../core/browser";
import { Razor } from "../razor/razor";

export class Dialog {
	static OnOpened = null;

	static DefaultOptions = {
		NegativeText: "No",
		AffirmativeText: "Yes",
		ButtonText: "Close",
		Size: "md",
		Icon: "",
		CentreHeader: false,
		ShowButtons: true,
		BackdropDismiss: true
	}

	static async Show(title, message, options) {
		return this.Open({
			Title: title,
			Message: message,
			Theme: "APPLICATION",
			NegativeText: "",
			AffirmativeText: "Ok",
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			...options
		});
	}

	static async ShowError(title, message, error, options) {
		if (error) {
			console.error(error);
		}

		document.querySelector(".dialog-box-error")?.closest("dialog")?.close();

		return this.Open({
			Title: title,
			Message: message,
			Icon: Browser.GetFlavour() !== "gov" ? "fas fa-exclamation-circle" : "",
			CentreHeader: Browser.GetFlavour() !== "gov",
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			Theme: "ERROR", ...options
		});
	}

	static async ShowImage(src, options) {
		return this.Open({
			Theme: "IMAGE",
			Centre: true,
			CustomHTML: document.createElementWithContents("img", null, { src }).outerHTML, ...options
		});
	}

	static async ShowWarning(title, message, options) {
		return this.Open({
			Title: title,
			Message: message,
			Icon: Browser.GetFlavour() !== "gov" ? "fas fa-exclamation-triangle" : "",
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			CentreHeader: Browser.GetFlavour() !== "gov",
			Theme: "WARNING", ...options
		});
	}

	static async ShowSuccess(title, message, options) {
		return this.Open({
			Title: title,
			Message: message,
			Icon: Browser.GetFlavour() !== "gov" ? "fas fa-check-circle" : "",
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			CentreHeader: Browser.GetFlavour() !== "gov",
			Theme: "SUCCESS", ...options
		});
	}

	static async ShowConfirmation(title, message, options) {
		return this.Open({
			Title: title,
			Message: message,
			CentreHeader: Browser.GetFlavour() !== "gov",
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			Icon: Browser.GetFlavour() !== "gov" ? "fas fa-question-circle" : "",
			Theme: "CONFIRM", ...options
		});
	}

	static async ShowInput(title, message, type, options) {
		return this.Open({
			Title: title,
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			Message: message,
			Theme: "INPUT",
			NegativeText: "Cancel",
			AffirmativeText: "Ok",
			InputType: type, ...options
		});
	}

	static async ShowTextBox(title, message, options) {
		return await this.ShowInput(title, message, "text", options);
	}

	static async ShowDatePicker(title, message, options) {
		return await this.ShowInput(title, message, "date", options);
	}

	static async ShowTimePicker(title, message, options) {
		return await this.ShowInput(title, message, "time", options);
	}

	static async ShowPassword(title, message, options) {
		return await this.ShowInput(title, message, "password", options);
	}

	static async ShowCustom(title, message, html, options) {
		return this.Open({
			Title: title,
			Message: message,
			CustomHTML: html,
			Theme: "SELECTION",
			Size: "xl",
			ButtonSide: Browser.GetFlavour() !== "gov" ? "RIGHT" : "LEFT",
			AffirmativeText: "Select",
			NegativeText: "Cancel", ...options
		});
	}

	static async ShowPartialView(title, message, viewName, viewOptions, options) {
		const html = await Razor.GetPartialView(viewName, viewOptions);

		return await this.ShowCustom(title, message, html, options);
	}

	static #GetValue(options, event) {
		switch (options.Theme.toUpperCase()) {
			case "SELECTION": {
				const row = document.querySelector(".dialog-custom table tr.active") || event?.detail.row;

				if (row) {
					return row.dataset.secondaryIdentifier ? `${row.dataset.identifier}|${row.dataset.secondaryIdentifier}` : row.dataset.identifier;
				} else {
					return null;
				}
			}
			case "INPUT":
				return document.querySelector(".dialog-input")?.value;
			default:
				return true;
		}
	}

	static #RefreshEvents(dialog, resolve, options) {
		const input = dialog.querySelector(".dialog-input");

		if (input) {
			Object.assign(input, options.InputAttributes,
				{
					value: options.DefaultValue || ""
				});

			if (options.DefaultValue && input.nodeName === "CHIP-INPUT") {
				input.onparsed = () => input.select();
			}
		}

		[...new Set(dialog.querySelectorAll(".dialog-input, chip-textarea"))].forEach(x => x.onkeyup = ev => {
			if (ev?.key === "Enter" && (ev.ctrlKey || (!options.Multiline && !["CHIP-TEXTAREA", "TEXTAREA"].includes(x.nodeName))) && x.value.trim() !== "" && options.OnCheckValid?.(dialog) !== false) {
				resolve(x.value);
				dialog.closest("dialog")?.close();
			}
		})

		const objectSearchbar = dialog.querySelector(".object-selection-search"),
			entryCount = dialog.querySelector(".entry-caption") ?? {};

		let table = null, listView = null;

		objectSearchbar?.focus();

		function refreshResolveEvents() {
			dialog.querySelectorAll(".dialog__selectable-option").forEach(x => x.onclick = () => {
				resolve(x.dataset.secondaryIdentifier ? `${x.dataset.identifier}|${x.dataset.secondaryIdentifier}` : x.dataset.identifier);
				dialog.closest("dialog")?.close();
			});

			table = dialog.querySelector(".dialog-custom table");
			listView = dialog.querySelector(".list-view");

			// Close the dialog and submit our value when a row has been picked
			table?.addEventListener("rowpicked", ev => {
				const row = dialog.querySelector(".dialog-custom table tr.active") || ev?.detail.row;

				if (row) {
					resolve(row.dataset.secondaryIdentifier ? `${row.dataset.identifier}|${row.dataset.secondaryIdentifier}` : row.dataset.identifier);
				} else {
					resolve(null);
				}

				dialog.closest("dialog")?.close();
			});

			listView?.addEventListener("click", ev => {
				const element = ev.target.closest("[data-identifier]");

				if (element) {
					resolve(element.dataset.secondaryIdentifier ? `${element.dataset.identifier}|${element.dataset.secondaryIdentifier}` : element.dataset.identifier);
					dialog.closest("dialog")?.close();
				}
			});
		}

		refreshResolveEvents();

		if (objectSearchbar) {
			let timeout;

			objectSearchbar.oninput = ev => {
				if (timeout) {
					clearTimeout(timeout);
				}

				timeout = setTimeout(() => {
					if (options.OnSearch) {
						Promise.resolve(options.OnSearch(dialog, ev.target.value)).then(() => refreshResolveEvents());
					} else {
						listView?.ListView.Search(ev.target.value);

						if (table) {
							table.Table.Search(ev.target.value);
							entryCount.textContent = `Showing 1-${table.Table.GetVisibleRows().length} of ${table.Table.GetAllRows().length}`;
						}
					}
				}, 500);
			}
		}

		options.OnRefreshEvents?.(dialog);
	}

	static Close() {
		document.querySelectorAll("dialog[open]").forEach(x => x.close());
	}

	static Open(options) {
		return new Promise(resolve => {
			const dialog = this.#RenderDialog({ ...this.DefaultOptions, ...options });

			dialog.querySelectorAll(".card-footer chip-button").forEach(btn => btn.onclick = ev => {
				if (btn.dataset.affirmative === "true") {
					if (options.OnCheckValid?.(dialog) === false) {
						ev.preventDefault();
					} else {
						resolve(this.#GetValue(options, ev));
						dialog.closest("dialog")?.close();
					}
				}

				if (btn.classList.contains("btn--dismiss-dialog")) {
					if (options.Theme === "CONFIRM") {
						resolve(btn.dataset.affirmative === "true");
					}

					dialog.closest("dialog")?.close();
				}
			});

			this.#RefreshEvents(dialog, resolve, options);

			options.OnBeforeOpen?.(dialog, resolve);
			this.OpenModal(dialog);

			dialog.closest("dialog")?.addEventListener("close", () => resolve(false));
		});
	}

	static OpenModal(element) {
		let focusElement = element.querySelector("chip-dropdown, chip-input, chip-textarea, chip-select") ?? [...element.querySelectorAll(".card-footer chip-button")].at(-1);

		const instance = document.createElementWithContents("dialog", element);
		document.body.appendChild(instance);

		instance.onclose = () => instance.remove();
		instance.showModal();

		if (focusElement) {
			focusElement.onparsed = () => focusElement.focus();
		}

		Dialog.OnOpened?.();
	}

	static #RenderDialog(options) {
		options.Appearance ??= options.Theme;

		// Custom HTML to insert into the dialog
		let custom = document.createElementWithContents("div", "", { className: "dialog-input-container" });

		if (options.Theme.toLowerCase() === "input") {
			custom.innerHTML = options.Multiline ? `<chip-textarea description="Enter value" rows="12" class="dialog-input"></chip-textarea>` : `<chip-input form-width description="Enter value" class="dialog-input" type="${options.InputType}"></chip-input>`;
		}

		if (options.CustomHTML instanceof Node) { custom.appendChild(options.CustomHTML); }
		else { custom.innerHTML += options.CustomHTML ?? ""; }

		if (options.Scrollable == null) {
			options.Scrollable = options.Size === "xl" || !custom.querySelector("chip-dropdown, chip-iconpicker, chip-colourpicker");
		}

		const details = {
			Icon: options.Icon?.trim() ? `<i class="mx-auto dialog-icon ${options.Icon}"></i>` : "",
			Title: options.Title?.toString().trim() ? `<div class="card-title">${options.Title}</div>` : "",
			Message: options.Message?.toString().trim() ? `<div class="card-subtitle">${options.Message}</div>` : ""
		}

		let header = "";

		if (options.Title?.toString().trim() !== "" || options.Message?.toString().trim() !== "" || ["SUCCESS", "ERROR", "WARNING"].includes(options.Theme.toUpperCase().trim())) {
			header =
				`<div class="card-header">
						${details.Icon}
            ${details.Title}
            ${details.Message}
          </div>`.trim();
		}

		const dialog = document.createElementWithContents("div",
			`
      <div class="modal-dialog${options.Centre ? " dialog-centred" : ""} modal-${options.Size}${options.FullHeight ? " modal-full-height" : ""}">
      ${options.Theme.toLowerCase() !== "image" ?
					`
          <div class="modal-content card-frame">
            ${header}
          <div class="card-body dialog-custom">${custom.outerHTML}`
					: `<div class="modal-content overflow-hidden">${options.CustomHTML ?? ""}</div>`}</div>
      `.trim(), {
			className: "dialog"
		});

		dialog.classList.add(`dialog-box-${options.Appearance.toLowerCase().replaceAll("_", "-")}`);

		if (options.Scrollable) { dialog.classList.add("dialog-scrollable") };
		if (options.CentreHeader) { dialog.classList.add("dialog-centre-header"); }


		// Footer buttons
		if (options.Theme.toLowerCase() !== "image" && options.ShowButtons) {
			let footerButtons = `<chip-button variation="none" class="btn--dismiss-dialog btn-dialog-themed">${options.ButtonText}</chip-button>`;

			if (!["SUCCESS", "ERROR", "WARNING"].includes(options.Theme.toUpperCase().trim())) {
				footerButtons = `
          ${options.NegativeText?.trim() !== "" ? `<div class="col"><chip-button variation="secondary" class="w-100 btn--dismiss-dialog">${options.NegativeText}</chip-button></div>` : ""}
          ${options.AffirmativeText?.trim() !== "" ? `<div class="col"><chip-button variation="theme" data-affirmative="true" class="btn-affirmative btn-dialog-themed">${options.AffirmativeText}</chip-button></div>` : ""}
          `.trim();
			}

			dialog.querySelector(".modal-content").appendChild(document.createElementWithContents("div", footerButtons, { className: `card-footer ${options.ButtonSide?.toUpperCase() === "RIGHT" ? "ms-auto" : "me-auto"} gx-2 row` }));
		}

		const closeCSSSelector = options.Theme.toLowerCase() !== "image" ? ".modal-content" : "img";

		dialog.onmousedown = ev => {
			if (options.BackdropDismiss && ev.target && !ev.target.closest?.(closeCSSSelector)) {
				dialog.onmouseup = event => {
					if (event.target && !event.target.closest?.(closeCSSSelector)) {
						dialog.closest("dialog")?.close();
					}

					dialog.onmouseup = null;
				}
			}
		}

		return dialog;
	}
}